import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { Box, Center, Grid, Radio, RadioGroup, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRole, ProjectUser } from "@nens/rana/api-v1";

import { Profile } from "@/lib/profile";
import { userRoleDescriptionString, userRoleString } from "@/translations";
import { getCompleteName } from "@/utils/get-complete-name";

interface ProjectUserRowWithRoleSelectionProps {
  user: ProjectUser["user"];
  role: ProjectRole;
  updateUserRole: (userId: string, role: ProjectRole) => void;
}

export function ProjectUserRowWithRoleSelection({
  user,
  role,
  updateUserRole,
}: ProjectUserRowWithRoleSelectionProps) {
  const intl = useIntl();
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  return (
    <Box>
      <Grid
        alignItems="center"
        templateColumns="auto 100px 240px 90px 1fr"
        borderWidth="base"
        borderColor="brand.grey.200"
        rounded="base"
        bgColor="brand.bg"
        fontSize={14}
        fontWeight={400}
        gap={4}
        p={4}
        cursor="pointer"
        title={intl.formatMessage({
          id: "title.click_to_change_user_role",
          defaultMessage: "Click to change user role",
        })}
        onClick={() => setShowRoleSelection(!showRoleSelection)}
      >
        <Profile user={user} />
        <Text fontWeight={600} color="brand.analytic">
          {getCompleteName(user)}
        </Text>
        <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {user.email}
        </Text>
        {
          <Text
            as="em"
            color="brand.clear_creek"
            visibility={showRoleSelection ? "hidden" : "visible"}
          >
            {userRoleString(intl, role)}
          </Text>
        }
        {showRoleSelection ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Grid>
      {showRoleSelection && (
        <RadioGroup display="flex" flexDir="column" mt={2} mb={4} gap={2} value={role}>
          {Object.values(ProjectRole).map((newRole) => (
            <Grid
              key={newRole}
              alignItems="center"
              templateColumns="40px 100px 340px 1fr"
              borderWidth="base"
              borderColor="brand.grey.200"
              rounded="base"
              bgColor={newRole === role ? "brand.cloudy" : "brand.bg"}
              fontSize={14}
              fontWeight={400}
              gap={4}
              p={4}
              _hover={{ bgColor: "gray.50" }}
              onClick={() => {
                updateUserRole(user.id, newRole);
                setShowRoleSelection(false);
              }}
              cursor="pointer"
            >
              <Center>
                <Radio value={newRole} />
              </Center>
              <Text>{userRoleString(intl, newRole)}</Text>
              <Text color="brand.grey.600">{userRoleDescriptionString(intl, newRole)}</Text>
              {newRole === role && (
                <Text as="em" color="brand.clear_creek">
                  <FormattedMessage id="role.active" defaultMessage="Active" />
                </Text>
              )}
            </Grid>
          ))}
        </RadioGroup>
      )}
    </Box>
  );
}
